import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { useFetch, useUser } from '../../../../shared';
import { DMTooltip, Message, IconButton, Icons, PageTitle } from '../../../../shared/components';
import apiRoutes from '../../../../../config/api/routes';
import { LogActionTypes, useDataLogging } from '../../../../shared/data-logging';
import RenameTrajectoryFormContent from './TrajectoryEditableTitleForm';
import { ITrajectoryDetail, TrajectoryViewMode } from 'src/config/api/models/Trajectory';
import { useSnackbar } from 'notistack';
import { Theme, Typography, styled, useTheme } from '@material-ui/core';

type Props = {
  onRenameTrajectory: () => void;
  bookId: string;
  trajectory: ITrajectoryDetail;
  viewMode: TrajectoryViewMode;
};

const StyledWrapper = styled('div')({
  marginTop: 0,
});

const StyledPageTitle = styled(PageTitle)<Theme>(({ theme }) => ({
  [theme.breakpoints.down('xs')]: {
    paddingLeft: 0,
  },
}));

const TrajectoryEditableTitle: FC<Props> = ({
  onRenameTrajectory,
  bookId,
  trajectory,
  viewMode,
}) => {
  const { isTeacher, user } = useUser();
  const log = useDataLogging();
  const { t } = useTranslation();
  const [editingTitle, setEditingTitle] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [tooltipOpened, setTooltipOpened] = useState(false);
  const [titleEdited, setTitleEdited] = useState(false);

  const handleCloseTooltip = () => {
    setTooltipOpened(false);
  };

  const handleOpenTooltip = () => {
    setTooltipOpened(true);
  };

  useEffect(() => {
    if (viewMode === 'read') {
      if (editingTitle === true && titleEdited === false) {
        log(LogActionTypes.CANCEL_TRAJECTORY_NAME_CHANGE, {
          trajectoryID: trajectory?.id,
          userId: user.id,
        });
      }
      setEditingTitle(false);
      // necessary between title renames.
      setTitleEdited(false);
    }
  }, [viewMode, editingTitle, titleEdited, log, trajectory.id, user.id]);

  const [renameTrajectoryResponse, renameTrajectory] = useFetch(
    apiRoutes.patchTrajectoryName(bookId, trajectory.id as string),
    {
      method: 'PATCH',
      // mapper: getPageMapper,
    }
  );

  useEffect(() => {
    if (renameTrajectoryResponse.success) {
      enqueueSnackbar('Titel hernoemd', {
        variant: 'success',
      });
      onRenameTrajectory();
      setTitleEdited(true);
    }
  }, [renameTrajectoryResponse, isTeacher, log, enqueueSnackbar, onRenameTrajectory]);

  if (viewMode === 'edit' && editingTitle) {
    return (
      <StyledWrapper>
        <Form
          onSubmit={(values: any) => {
            log(LogActionTypes.SAVE_TRAJECTORY_NAME, {
              trajectoryID: trajectory?.id,
              userId: user.id,
            });
            setEditingTitle(false);
            renameTrajectory({
              body: {
                alternative_title: values.title,
              },
            });
          }}
          initialValues={{
            title: trajectory.parentId ? trajectory.parentTitle : trajectory.title,
          }}
          render={({ handleSubmit, form }) => (
            <>
              <RenameTrajectoryFormContent
                handleSubmit={(...args) => {
                  handleSubmit(...args);
                }}
                handleReset={() => {
                  form.reset();
                  form.change('title', trajectory.parentTitle ?? trajectory.title);
                  log(LogActionTypes.REVERT_TRAJECTORY_NAME_TO_DEFAULT, {
                    trajectoryID: trajectory?.id,
                    userId: user.id,
                  });
                }}
              />
              {renameTrajectoryResponse && renameTrajectoryResponse.error && (
                <Message type="error">{t(renameTrajectoryResponse.reason)}</Message>
              )}
            </>
          )}
        />
      </StyledWrapper>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'row' }}>
      <StyledPageTitle prefix={undefined} marginBottom={isTeacher ? 1 : 3}>
        {trajectory.parentId ? trajectory.parentTitle : trajectory.title}
      </StyledPageTitle>

      {/* Owned Differentiation shows icon and tooltip */}
      {trajectory.parentId && trajectory.isOwner && (
        <div
          style={{ cursor: 'pointer' }}
          onMouseLeave={(e: any) => {
            handleCloseTooltip();
          }}
          onMouseEnter={(e: any) => {
            if (viewMode === 'read') handleOpenTooltip();
          }}
          onClick={() => {
            if (viewMode === 'edit') {
              setEditingTitle(true);
              log(LogActionTypes.START_TRAJECTORY_NAME_CHANGE, {
                trajectoryID: trajectory?.id,
                userId: user?.id,
              });
            }
          }}
        >
          <DMTooltip
            condition={trajectory.parentTitle != null}
            controlled
            opened={tooltipOpened}
            onClose={handleCloseTooltip}
            onOpen={handleOpenTooltip}
            message={
              <>
                <Typography>{`Oorpronkelijke titel: ${trajectory.parentTitle}`}</Typography>
                <Typography style={{ whiteSpace: 'break-spaces' }}>
                  Groepen waaraan je dit traject toewijst, zien jouw aangepaste titel
                </Typography>
              </>
            }
            placement="right-end"
          >
            <div id="tooltip-ref-fix">
              <IconButton
                icon={trajectory.parentTitle ? Icons.EDIT_TITLE : Icons.EDIT}
                color={
                  viewMode === 'read' ? theme.palette.grey['500'] : theme.palette.secondary.dark
                }
                largeIcon
              />
            </div>
          </DMTooltip>
        </div>
      )}
    </div>
  );
};

export default TrajectoryEditableTitle;
